import React from "react";
import { Users, Lock, Pencil, Trash2 } from "lucide-react";
import { motion } from "framer-motion";

function TableAction({ t, user, onJoin, onPay }) {
  const me = user && t.members.find(m => m.user_id === user.id);
  if (me?.paid) return <span className="font-mono text-xs text-neon tracking-widest">✓ PAGATO</span>;
  if (me) return <button onClick={()=>onPay(t)} className="btn-primary !py-2 !px-3 text-xs" data-testid={`pay-${t.id}`}>PAGA CAPARRA</button>;
  if (t.members.length >= t.capacity) return <span className="font-mono text-xs text-alert tracking-widest">COMPLETO</span>;
  return <button onClick={()=>onJoin(t)} className="btn-ghost !py-2 !px-3 text-xs" data-testid={`join-${t.id}`}>UNISCITI</button>;
}

function MemberChips({ members }) {
  return (
    <div className="mt-4 flex flex-wrap gap-1">
      {members.map(m => (
        <span key={m.user_id} className={`font-mono text-[10px] tracking-widest px-2 py-1 border ${m.paid?"border-neon text-neon":"border-neutral-700 text-neutral-400"}`}>
          {m.name.split(" ")[0].toUpperCase()}{m.paid?" ✓":""}
        </span>
      ))}
    </div>
  );
}

function PriceBreakdown({ t }) {
  if (t.table_price == null) {
    return t.price_per_person != null ? <div className="font-mono text-[10px] text-neutral-400 tracking-widest mb-1" data-testid={`table-price-${t.id}`}>PREZZO TOT. €{t.price_per_person}/PERSONA</div> : null;
  }
  return (
    <div className="font-mono text-[10px] text-neutral-400 tracking-widest mb-2 space-y-0.5" data-testid={`table-price-${t.id}`}>
      <div>€{t.entry_price} INGRESSO · €{t.table_price} TAVOLO</div>
      <div className="text-white">TOTALE €{t.price_per_person}/PERSONA</div>
    </div>
  );
}

function ManageButtons({ t, onEdit, onDelete }) {
  return (
    <div className="flex gap-2">
      <button onClick={()=>onEdit(t)} className="text-neutral-500 hover:text-neon transition-colors" aria-label="Modifica" data-testid={`edit-table-${t.id}`}><Pencil className="w-4 h-4"/></button>
      <button onClick={()=>onDelete(t)} className="text-neutral-500 hover:text-alert transition-colors" aria-label="Elimina" data-testid={`delete-table-${t.id}`}><Trash2 className="w-4 h-4"/></button>
    </div>
  );
}

export default function TableCard({ t, index, user, onJoin, onPay, onEdit, onDelete }) {
  const paid = t.members.filter(m=>m.paid).length;
  const canManage = user && (user.role === "admin" || (user.role === "affiliate" && t.owner_id === user.id));
  return (
    <motion.div initial={{opacity:0,y:16}} animate={{opacity:1,y:0}} transition={{delay:index*0.05}}
      className="card-dark p-6 flex flex-col" data-testid={`table-card-${t.id}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="label-mono text-neon mb-1">{t.event_title}</div>
          <h3 className="font-display text-2xl">{t.name}</h3>
        </div>
        <div className="flex items-center gap-3">
          {!t.is_public && <Lock className="w-4 h-4 text-alert"/>}
          {canManage && <ManageButtons t={t} onEdit={onEdit} onDelete={onDelete}/>}
        </div>
      </div>
      <div className="font-mono text-xs text-neutral-500 tracking-widest mt-2">
        {new Date(t.event_date).toLocaleDateString("it-IT",{day:"2-digit",month:"short"})} · {t.venue}
      </div>
      {t.note && <p className="text-sm text-neutral-400 mt-3">{t.note}</p>}
      <div className="mt-5 flex items-center gap-4 pb-5 border-b border-neutral-800">
        <div className="flex items-center gap-2"><Users className="w-4 h-4 text-neon"/><span className="font-mono text-sm">{t.members.length}/{t.capacity}</span></div>
        <div className="font-mono text-xs text-neutral-500">PAGATI: <span className="text-neon">{paid}</span></div>
      </div>
      <div className="mt-5 flex items-center justify-between">
        <div>
          <PriceBreakdown t={t}/>
          <div className="label-mono text-[9px]">CAPARRA/PERSONA</div>
          <div className="font-display text-2xl text-neon">€{t.deposit_per_person}</div>
          <div className="label-mono text-[9px] mt-1" data-testid={`table-payee-${t.id}`}>INCASSA: {t.payee_name || "AFTERLIST"}</div>
        </div>
        <TableAction t={t} user={user} onJoin={onJoin} onPay={onPay}/>
      </div>
      <MemberChips members={t.members}/>
    </motion.div>
  );
}
